import { useState, useMemo } from "react";
import {
  useIamPlatforms,
  useIamRoles,
  useIamPermissions,
  useIamModules,
  useIamAccessManifest,
  useIamAuditEvents,
} from "@/hooks";
import type {
  IamPlatformApi,
  IamRoleApi,
  IamPermissionApi,
  IamModuleApi,
  IamAuditEventApi,
} from "@/lib/api";

type SubTab = "roles" | "permissions" | "manifest" | "audit";

export default function IamAdminPanel() {
  const [subTab, setSubTab] = useState<SubTab>("roles");
  const [platformFilter, setPlatformFilter] = useState<string>("");

  const { platforms, loading: pLoading } = useIamPlatforms();
  const { roles, loading: rLoading } = useIamRoles();
  const { permissions, loading: permLoading } = useIamPermissions();
  const { modules, loading: mLoading } = useIamModules();
  const { manifest, loading: manLoading } = useIamAccessManifest();
  const { events, loading: aLoading } = useIamAuditEvents({ limit: 50 });

  const loading = pLoading || rLoading || permLoading || mLoading;

  const filteredRoles = useMemo(() => {
    if (!platformFilter) return roles;
    const plat = platforms.find((p) => p.code === platformFilter);
    if (!plat) return roles;
    return roles.filter((r) => r.platform_id === plat.id);
  }, [roles, platformFilter, platforms]);

  const filteredPermissions = useMemo(() => {
    if (!platformFilter) return permissions;
    const plat = platforms.find((p) => p.code === platformFilter);
    if (!plat) return permissions;
    const modIds = new Set(modules.filter((m) => m.platform_id === plat.id).map((m) => m.id));
    return permissions.filter((p) => modIds.has(p.module_id));
  }, [permissions, platformFilter, platforms, modules]);

  const subTabs: { id: SubTab; label: string; icon: string }[] = [
    { id: "roles", label: "Papeis", icon: "ri-shield-user-line" },
    { id: "permissions", label: "Permissoes", icon: "ri-lock-line" },
    { id: "manifest", label: "Meu Acesso", icon: "ri-key-2-line" },
    { id: "audit", label: "Auditoria IAM", icon: "ri-file-list-3-line" },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground-900">
            Controle de Acesso (IAM)
          </h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            Sistema de controle de acesso granular: plataformas, papeis, permissoes, escopos e auditoria
          </p>
        </div>
        {/* Platform filter */}
        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="text-xs border border-background-200 rounded-lg px-3 py-1.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="">Todas as plataformas</option>
          {platforms.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {/* Platform cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {platforms.map((p) => (
          <PlatformCard key={p.id} platform={p} />
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-background-200 overflow-x-auto">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
              subTab === t.id
                ? "text-primary-700 border-primary-500 bg-primary-50/50"
                : "text-foreground-500 border-transparent hover:text-foreground-700"
            }`}
          >
            <i className={`${t.icon} mr-1`} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-foreground-400 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2" /> Carregando dados IAM...
        </div>
      )}

      {!loading && subTab === "roles" && (
        <RolesTable roles={filteredRoles} platforms={platforms} />
      )}

      {!loading && subTab === "permissions" && (
        <PermissionsTable
          permissions={filteredPermissions}
          modules={modules}
          platforms={platforms}
        />
      )}

      {!loading && subTab === "manifest" && (
        <ManifestView manifest={manifest} loading={manLoading} />
      )}

      {!loading && subTab === "audit" && (
        <AuditEventsTable events={events} loading={aLoading} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PlatformCard({ platform }: { platform: IamPlatformApi }) {
  const iconMap: Record<string, string> = {
    FLORAIQ_WEB: "ri-computer-line",
    FLORAIQ_APP: "ri-smartphone-line",
    FLORAIQ_ADMIN: "ri-settings-3-line",
    FLORAIQ_AUDIT: "ri-file-shield-line",
  };
  return (
    <div className="bg-white border border-background-200 rounded-xl p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
        <i className={`${iconMap[platform.code] ?? "ri-apps-line"} text-primary-600 text-lg`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-foreground-800 truncate">{platform.name}</p>
        <p className="text-[10px] text-foreground-400 font-mono">{platform.code}</p>
      </div>
      <span
        className={`ml-auto shrink-0 w-2 h-2 rounded-full ${
          platform.is_active ? "bg-green-500" : "bg-red-400"
        }`}
        title={platform.is_active ? "Ativa" : "Inativa"}
      />
    </div>
  );
}

function RolesTable({
  roles,
  platforms,
}: {
  roles: IamRoleApi[];
  platforms: IamPlatformApi[];
}) {
  const platMap = useMemo(() => {
    const m: Record<number, string> = {};
    platforms.forEach((p) => (m[p.id] = p.name));
    return m;
  }, [platforms]);

  if (roles.length === 0) {
    return (
      <p className="text-xs text-foreground-400 py-6 text-center">
        Nenhum papel encontrado para o filtro selecionado.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-background-200 text-left text-foreground-500">
            <th className="py-2 px-3 font-medium">Codigo</th>
            <th className="py-2 px-3 font-medium">Nome</th>
            <th className="py-2 px-3 font-medium">Plataforma</th>
            <th className="py-2 px-3 font-medium">Sistema</th>
            <th className="py-2 px-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {roles.map((r) => (
            <tr key={r.id} className="border-b border-background-100 hover:bg-background-50/50">
              <td className="py-2 px-3 font-mono text-foreground-600">{r.code}</td>
              <td className="py-2 px-3 text-foreground-800">{r.name}</td>
              <td className="py-2 px-3 text-foreground-500">{platMap[r.platform_id] ?? "—"}</td>
              <td className="py-2 px-3">
                {r.is_system ? (
                  <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">SISTEMA</span>
                ) : (
                  <span className="text-[10px] bg-background-100 text-foreground-500 px-1.5 py-0.5 rounded">CUSTOM</span>
                )}
              </td>
              <td className="py-2 px-3">
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    r.is_active
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {r.is_active ? "ATIVO" : "INATIVO"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PermissionsTable({
  permissions,
  modules,
  platforms,
}: {
  permissions: IamPermissionApi[];
  modules: IamModuleApi[];
  platforms: IamPlatformApi[];
}) {
  const modMap = useMemo(() => {
    const m: Record<number, IamModuleApi> = {};
    modules.forEach((mod) => (m[mod.id] = mod));
    return m;
  }, [modules]);

  const platMap = useMemo(() => {
    const m: Record<number, string> = {};
    platforms.forEach((p) => (m[p.id] = p.name));
    return m;
  }, [platforms]);

  if (permissions.length === 0) {
    return (
      <p className="text-xs text-foreground-400 py-6 text-center">
        Nenhuma permissao encontrada.
      </p>
    );
  }

  const grouped = useMemo(() => {
    const g: Record<string, IamPermissionApi[]> = {};
    permissions.forEach((p) => {
      const mod = modMap[p.module_id];
      const key = mod ? mod.name : "Sem modulo";
      if (!g[key]) g[key] = [];
      g[key].push(p);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [permissions, modMap]);

  return (
    <div className="space-y-4">
      {grouped.map(([moduleName, perms]) => {
        const firstPerm = perms[0];
        const mod = firstPerm ? modMap[firstPerm.module_id] : undefined;
        const platName = mod ? platMap[mod.platform_id] ?? "" : "";
        return (
          <div key={moduleName}>
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-xs font-semibold text-foreground-700">{moduleName}</h4>
              {platName && (
                <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">
                  {platName}
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-background-200 text-left text-foreground-500">
                    <th className="py-1.5 px-3 font-medium">Codigo</th>
                    <th className="py-1.5 px-3 font-medium">Nome</th>
                    <th className="py-1.5 px-3 font-medium">Acao</th>
                    <th className="py-1.5 px-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {perms.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-background-100 hover:bg-background-50/50"
                    >
                      <td className="py-1.5 px-3 font-mono text-foreground-600">{p.code}</td>
                      <td className="py-1.5 px-3 text-foreground-800">{p.name}</td>
                      <td className="py-1.5 px-3">
                        <span className="text-[10px] bg-background-100 text-foreground-500 px-1.5 py-0.5 rounded">
                          {p.action_type}
                        </span>
                      </td>
                      <td className="py-1.5 px-3">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                            p.is_active
                              ? "bg-green-50 text-green-700"
                              : "bg-red-50 text-red-600"
                          }`}
                        >
                          {p.is_active ? "ATIVO" : "INATIVO"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ManifestView({
  manifest,
  loading,
}: {
  manifest: {
    user_id: number;
    organization_id: number;
    platforms: Record<
      string,
      {
        roles: string[];
        permissions: string[];
        scopes: Array<{ scope_type: string; reference_id: number | null }>;
      }
    >;
  } | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-foreground-400 text-sm">
        <i className="ri-loader-4-line animate-spin mr-2" /> Carregando manifesto...
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className="text-center py-12">
        <i className="ri-key-2-line text-3xl text-foreground-300 mb-2 block" />
        <p className="text-xs text-foreground-400">
          Sem vinculo organizacional ativo. O manifesto de acesso nao pode ser carregado.
        </p>
      </div>
    );
  }

  const platformEntries = Object.entries(manifest.platforms);

  return (
    <div className="space-y-4">
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-3">
        <div className="flex items-center gap-2 text-xs text-primary-700">
          <i className="ri-information-line text-base" />
          <span>
            Manifesto de acesso do usuario <strong>#{manifest.user_id}</strong> na organizacao{" "}
            <strong>#{manifest.organization_id}</strong>
          </span>
        </div>
      </div>

      {platformEntries.length === 0 ? (
        <p className="text-xs text-foreground-400 text-center py-6">
          Nenhuma plataforma atribuida.
        </p>
      ) : (
        platformEntries.map(([platCode, data]) => (
          <div
            key={platCode}
            className="bg-white border border-background-200 rounded-xl p-4 space-y-3"
          >
            <h4 className="text-xs font-semibold text-foreground-800 flex items-center gap-2">
              <i className="ri-apps-line text-primary-500" />
              {platCode}
            </h4>

            {/* Roles */}
            <div>
              <p className="text-[10px] text-foreground-500 uppercase tracking-wider mb-1">Papeis</p>
              <div className="flex flex-wrap gap-1">
                {data.roles.length === 0 ? (
                  <span className="text-xs text-foreground-300">Nenhum</span>
                ) : (
                  data.roles.map((r) => (
                    <span
                      key={r}
                      className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded"
                    >
                      {r}
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <p className="text-[10px] text-foreground-500 uppercase tracking-wider mb-1">
                Permissoes ({data.permissions.length})
              </p>
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                {data.permissions.map((p) => (
                  <span
                    key={p}
                    className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-mono"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>

            {/* Scopes */}
            {data.scopes.length > 0 && (
              <div>
                <p className="text-[10px] text-foreground-500 uppercase tracking-wider mb-1">
                  Escopos
                </p>
                <div className="flex flex-wrap gap-1">
                  {data.scopes.map((s, i) => (
                    <span
                      key={i}
                      className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded"
                    >
                      {s.scope_type}
                      {s.reference_id != null && ` #${s.reference_id}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function AuditEventsTable({
  events,
  loading,
}: {
  events: IamAuditEventApi[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-foreground-400 text-sm">
        <i className="ri-loader-4-line animate-spin mr-2" /> Carregando auditoria...
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="ri-file-list-3-line text-3xl text-foreground-300 mb-2 block" />
        <p className="text-xs text-foreground-400">Nenhum evento de auditoria IAM registrado.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-background-200 text-left text-foreground-500">
            <th className="py-2 px-3 font-medium">Data</th>
            <th className="py-2 px-3 font-medium">Tipo</th>
            <th className="py-2 px-3 font-medium">Ator</th>
            <th className="py-2 px-3 font-medium">Alvo</th>
            <th className="py-2 px-3 font-medium">Permissao</th>
            <th className="py-2 px-3 font-medium">IP</th>
            <th className="py-2 px-3 font-medium">Motivo</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} className="border-b border-background-100 hover:bg-background-50/50">
              <td className="py-2 px-3 text-foreground-500 whitespace-nowrap">
                {e.created_at ? new Date(e.created_at).toLocaleString("pt-BR") : "—"}
              </td>
              <td className="py-2 px-3">
                <EventTypeBadge type={e.event_type} />
              </td>
              <td className="py-2 px-3 text-foreground-600">
                {e.actor_user_id ? `#${e.actor_user_id}` : "—"}
              </td>
              <td className="py-2 px-3 text-foreground-600">
                {e.target_user_id ? `#${e.target_user_id}` : "—"}
              </td>
              <td className="py-2 px-3 font-mono text-foreground-500">
                {e.permission_code ?? "—"}
              </td>
              <td className="py-2 px-3 text-foreground-400">{e.ip_address ?? "—"}</td>
              <td className="py-2 px-3 text-foreground-500 max-w-[200px] truncate">
                {e.reason ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventTypeBadge({ type }: { type: string }) {
  const colorMap: Record<string, string> = {
    PERMISSION_GRANTED: "bg-green-50 text-green-700",
    PERMISSION_DENIED: "bg-red-50 text-red-600",
    ROLE_ASSIGNED: "bg-blue-50 text-blue-700",
    ROLE_REVOKED: "bg-amber-50 text-amber-700",
    MEMBERSHIP_CREATED: "bg-purple-50 text-purple-700",
    SCOPE_CHANGED: "bg-cyan-50 text-cyan-700",
  };
  const cls = colorMap[type] ?? "bg-background-100 text-foreground-500";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${cls}`}>
      {type}
    </span>
  );
}
